import { useCallback, useState } from 'react';
import { Icon, Sheet } from '@astral/ui';
import { LANGUAGE_META } from '@astral/shared';
import { openTelegramLink } from '@astral/telegram/webapp';
import { useAuth } from '../providers/AuthProvider.js';
import { useI18n, useT } from '../providers/I18nProvider.js';
import { CelestialHero } from '../components/CelestialHero.js';
import { SectionOrnament } from '../components/SectionOrnament.js';
import { LanguagePicker } from '../components/LanguagePicker.js';
import { JoinChannelSheet } from '../components/JoinChannelSheet.js';
import { api } from '../lib/api.js';
import { ASTRAL_CHANNEL_USERNAME, telegramLink } from '../config.js';

export interface OnboardingScreenProps {
  /** Starts the controlled entry → Home transition (owned by App). */
  onEnter: () => void;
  /** True once entry has begun — locks the statue against a second activation. */
  entering?: boolean;
}

/**
 * First entry — an atmospheric gateway, not a settings form.
 *
 * The statue itself is the way in: tapping it plays a quiet celestial
 * activation and hands off to the controlled transition. There is deliberately
 * no large Enter CTA; the localized instruction line is the discoverability cue,
 * backed by a slow idle glow behind the artwork.
 *
 * Language stays reachable through the same compact pill Home uses. Theme
 * selection lives in Settings.
 */
export function OnboardingScreen({ onEnter, entering = false }: OnboardingScreenProps): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const { updatePreferences } = useAuth();
  const [langSheet, setLangSheet] = useState(false);

  /*
   * THE MEMBERSHIP GATE.
   *
   * Deliberately layered ON TOP of the existing entry, not woven into it: the
   * statue, its press/halo/crossfade and `onEnter` are all untouched, and
   * `onEnter()` is still what runs the transition. This only decides WHETHER to
   * call it. Membership itself is decided by the server from the verified
   * session's Telegram id — nothing here is trusted.
   */
  const [gate, setGate] = useState<'idle' | 'checking' | 'blocked'>('idle');
  const [unavailable, setUnavailable] = useState(false);
  const [channel, setChannel] = useState(`@${ASTRAL_CHANNEL_USERNAME}`);

  /**
   * Resolve membership and act on it.
   *
   * `fresh` is what "I've joined — Check again" passes: it hits the endpoint
   * that bypasses the server's cache, so a user who joined ten seconds ago is
   * never refused by an answer cached before they did.
   */
  const resolve = useCallback(
    async (fresh: boolean): Promise<void> => {
      setGate('checking');
      setUnavailable(false);
      try {
        const status = fresh ? await api.recheckMembership() : await api.membership();
        setChannel(status.channel);
        if (status.state === 'JOINED') {
          setGate('idle');
          onEnter();
          return;
        }
        // NOT_JOINED and UNAVAILABLE both stop entry, but they say different
        // things — one is "go and join", the other is "we could not check".
        setUnavailable(status.state === 'UNAVAILABLE');
        setGate('blocked');
      } catch {
        /*
         * The check itself failed (offline, 5xx, rate-limited). That is not
         * evidence the user is a non-member, so it is presented as the
         * temporary condition rather than as a refusal.
         */
        setUnavailable(true);
        setGate('blocked');
      }
    },
    [onEnter],
  );

  return (
    <div className="screen onboarding">
      {/* Identity + the same compact language pill Home uses. */}
      <header className="onboarding__brand">
        <button className="lang-pill onboarding__lang" onClick={() => setLangSheet(true)} aria-label={t('settings.language')}>
          <Icon name="globe" size={14} />
          <span className="lang-pill__label">{LANGUAGE_META[language].nativeName}</span>
          <Icon name="chevron-down" size={13} />
        </button>
        <span className="onboarding__mark metallic-text">Astral</span>
        <span className="onboarding__kicker">{t('onboarding.welcomeSubtitle')}</span>
      </header>

      {/* The statue is the activation target. */}
      <CelestialHero
        variant="entry"
        onActivate={() => void resolve(false)}
        activated={entering || gate === 'checking'}
        activateLabel={t('onboarding.touchStatue')}
      />

      {/* Welcome region — instruction replaces the old Enter card. */}
      <div className="entry-panel">
        <div className="entry-panel__head">
          <SectionOrnament reverse />
          <span className="entry-panel__title">{t('home.introTitle')}</span>
          <SectionOrnament />
        </div>
        <p className="entry-panel__body">{t('home.introBody')}</p>
        <p className="entry-hint">
          <Icon name="sparkle-4" size={13} />
          <span>{t('onboarding.touchStatue')}</span>
        </p>
      </div>

      {/* Joining is REQUIRED to use Sticker Studio — the card says so, and the
          statue tap enforces it against the server. */}
      <button
        className="entry-channel"
        aria-label={t('channels.open')}
        onClick={() => openTelegramLink(telegramLink(ASTRAL_CHANNEL_USERNAME))}
      >
        <span className="entry-channel__icon">
          <Icon name="telegram" size={17} />
        </span>
        <span className="entry-channel__text">
          <span className="entry-channel__name">@{ASTRAL_CHANNEL_USERNAME}</span>
          <span className="entry-channel__sub">{t('onboarding.channelPrompt')}</span>
        </span>
        <Icon name="chevron-right" size={15} className="chev" />
      </button>

      <JoinChannelSheet
        open={gate === 'blocked'}
        channel={channel}
        checking={gate === 'checking'}
        unavailable={unavailable}
        onCheckAgain={() => void resolve(true)}
        onClose={() => setGate('idle')}
      />

      {/* The same picker Home opens — one language implementation, not two. */}
      <Sheet open={langSheet} onClose={() => setLangSheet(false)} title={t('settings.language')}>
        <LanguagePicker
          value={language}
          onSelect={(lang) => {
            void updatePreferences({ language: lang });
            setLangSheet(false);
          }}
        />
      </Sheet>
    </div>
  );
}
